import { faChevronDown, faChevronRight, faCircleInfo, faMagnifyingGlass, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ApiService, type FilterConfig, type FilterOption } from '@supply-mover-2-0/core';
import { DIRECT_TEAM_PREFIX, teamPickToken, type TeamSummaryDto } from '@supply-mover-2-0/shared';
import type React from 'react';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useThemeColors } from '../../providers/ThemeProvider';
import { useTranslation } from '../../providers/TranslationProvider';
import { cn } from '../../utils/cn';
import { Tooltip } from '../overlays/Tooltip';
import { Checkbox } from '../forms/Checkbox';
import { FormField } from '../forms/FormField';
import { Toggle } from '../forms/Toggle';

interface BaseTeamTreeSelectProps {
	filterType: string;
	config: FilterConfig;
	selectedValues: Array<FilterOption>;
	onValuesChange: (values: Array<FilterOption>) => void;
	disabled?: boolean;
	isModalOpen?: boolean;
	size?: 'xs' | 'sm' | 'md' | 'lg';
	layout?: 'sidebar' | 'tray' | 'inline' | 'bar';
}

export interface TeamFilterNodeModel {
	team: TeamSummaryDto;
	children: Array<TeamFilterNodeModel>;
}

const idOf = (option: FilterOption): number => Number(String(option.id).replace(DIRECT_TEAM_PREFIX, ''));

/**
 * PD-17521 — the teams a pick of `node` with its sub-teams already covers:
 * everything beneath it except specialty (overlay) teams and what sits under
 * them. The server's roll-up leaves those out (decision #34), so they are
 * never shown as included and stay pickable on their own.
 */
export function coveredByPick(node: TeamFilterNodeModel): Array<number> {
	return node.children
		.filter((child) => child.team.is_overlay !== true)
		.flatMap((child) => [child.team.team_id, ...coveredByPick(child)]);
}

/**
 * PD-17521 — the "Include sub-teams" switch as it should read: from the
 * picks when there are any, otherwise the last setting (which follows the
 * picks), so clearing a saved "only" pick does not turn it back on.
 */
export function includeSubTeamsFor(selectedValues: ReadonlyArray<FilterOption>, lastSetting: boolean): boolean {
	return selectedValues.length > 0
		? !selectedValues.some((option) => String(option.id).startsWith(DIRECT_TEAM_PREFIX))
		: lastSetting;
}

/** The teams as a tree, parents first, by name; a team whose parent is not offered sits at the top. */
export function buildTeamTree(teams: ReadonlyArray<TeamSummaryDto>): Array<TeamFilterNodeModel> {
	const offered = new Set(teams.map((team) => team.team_id));
	const byParent = new Map<number | null, Array<TeamSummaryDto>>();
	for (const team of teams) {
		const parent = team.parent_team_id != null && offered.has(team.parent_team_id) ? team.parent_team_id : null;
		byParent.set(parent, [...(byParent.get(parent) ?? []), team]);
	}
	const build = (parent: number | null, seen: Set<number>): Array<TeamFilterNodeModel> =>
		(byParent.get(parent) ?? [])
			.filter((team) => !seen.has(team.team_id))
			.sort((a, b) => a.team_name.localeCompare(b.team_name))
			.map((team) => ({ team, children: build(team.team_id, new Set([...seen, team.team_id])) }));
	return build(null, new Set());
}

/**
 * PD-17435 — the Teams filter on reports and dashboard widgets as a tree:
 * tick a region or a single team; with "Include sub-teams" on (the default)
 * a pick counts everything beneath it, off it counts that team's own people
 * (sent as "=<id>"). The teams offered are the ones the server lets the
 * reader pick; when there are none (a salesperson limited to their own
 * customers) the filter is not shown.
 */
export function BaseTeamTreeSelect({
	filterType,
	config,
	selectedValues,
	onValuesChange,
	disabled = false,
	isModalOpen = false,
	size = 'md',
	layout = 'inline',
}: BaseTeamTreeSelectProps): React.JSX.Element | null {
	const colors = useThemeColors();
	const { t } = useTranslation();
	const [teams, setTeams] = useState<Array<TeamSummaryDto> | null>(null);
	const [open, setOpen] = useState(false);
	const [search, setSearch] = useState('');
	const [collapsed, setCollapsed] = useState<Set<number>>(new Set());
	const wrapperRef = useRef<HTMLDivElement>(null);
	const panelRef = useRef<HTMLDivElement>(null);
	const [anchor, setAnchor] = useState<{ top: number; left: number } | null>(null);
	// The switch's own setting, for when nothing is picked yet: with no picks
	// there is no token to read it from, and the next pick is made with it.
	const [subTeamsSetting, setSubTeamsSetting] = useState(true);
	// PD-17521 — follow the picks (a saved "only" pick included), so the
	// setting survives the picks being cleared.
	const picksSetting = selectedValues.length > 0 ? includeSubTeamsFor(selectedValues, true) : null;
	useEffect(() => {
		if (picksSetting != null) {
			setSubTeamsSetting(picksSetting);
		}
	}, [picksSetting]);

	useEffect(() => {
		let cancelled = false;
		ApiService.apiGet<{ success?: boolean; data?: Array<TeamSummaryDto> }>(config.apiEndpoint ?? '/reporting/team-options')
			.then((response) => {
				if (!cancelled) {
					setTeams(Array.isArray(response?.data) ? response.data : []);
				}
			})
			.catch(() => {
				if (!cancelled) {
					setTeams([]);
				}
			});
		return () => {
			cancelled = true;
		};
	}, [config.apiEndpoint]);

	useEffect(() => {
		if (!open) {
			return;
		}
		const close = (event: MouseEvent): void => {
			const target = event.target as Node;
			if (wrapperRef.current?.contains(target) !== true && panelRef.current?.contains(target) !== true) {
				setOpen(false);
			}
		};
		document.addEventListener('mousedown', close);
		return () => document.removeEventListener('mousedown', close);
	}, [open]);

	// The panel floats above the page (a report's filter bar clips what
	// overflows it), anchored under the field and kept on screen.
	useLayoutEffect(() => {
		if (!open || layout === 'tray' || layout === 'sidebar') {
			return;
		}
		const place = (): void => {
			const rect = wrapperRef.current?.getBoundingClientRect();
			if (rect != null) {
				setAnchor({ top: rect.bottom + 4, left: Math.max(8, Math.min(rect.left, window.innerWidth - 348)) });
			}
		};
		place();
		window.addEventListener('resize', place);
		window.addEventListener('scroll', place, true);
		return () => {
			window.removeEventListener('resize', place);
			window.removeEventListener('scroll', place, true);
		};
	}, [open, layout]);

	const tree = useMemo(() => (teams == null ? [] : buildTeamTree(teams)), [teams]);
	const byId = useMemo(() => new Map((teams ?? []).map((team) => [team.team_id, team])), [teams]);
	const selectedIds = useMemo(() => new Set(selectedValues.map(idOf)), [selectedValues]);
	const includeSubTeams = includeSubTeamsFor(selectedValues, subTeamsSetting);

	if (teams != null && teams.length === 0) {
		return null;
	}

	const optionFor = (team: TeamSummaryDto, withSubTeams: boolean): FilterOption => ({
		id: teamPickToken(team.team_id, withSubTeams),
		label: team.team_name,
	});

	const toggle = (node: TeamFilterNodeModel): void => {
		const id = node.team.team_id;
		if (selectedIds.has(id)) {
			onValuesChange(selectedValues.filter((option) => idOf(option) !== id));
			return;
		}
		// With sub-teams included, a pick already covers its sub-teams: drop any picked separately.
		const covered = includeSubTeams ? new Set(coveredByPick(node)) : new Set<number>();
		onValuesChange([...selectedValues.filter((option) => !covered.has(idOf(option))), optionFor(node.team, includeSubTeams)]);
	};

	const setIncludeSubTeams = (on: boolean): void => {
		setSubTeamsSetting(on);
		if (selectedValues.length === 0) {
			return;
		}
		onValuesChange(
			selectedValues.map((option) => {
				const team = byId.get(idOf(option));
				return team == null ? option : optionFor(team, on);
			}),
		);
	};

	const needle = search.trim().toLowerCase();
	const matches = (node: TeamFilterNodeModel): boolean =>
		node.team.team_name.toLowerCase().includes(needle) || node.children.some(matches);

	const renderNode = (node: TeamFilterNodeModel, depth: number, impliedByParent: boolean): React.ReactNode => {
		if (needle !== '' && !matches(node)) {
			return null;
		}
		const id = node.team.team_id;
		const checked = selectedIds.has(id) || impliedByParent;
		const hasChildren = node.children.length > 0;
		const expanded = needle !== '' || !collapsed.has(id);
		const detail = [node.team.team_type_name, node.team.member_count != null ? String(node.team.member_count) : null].filter((part) => part != null && part !== '').join(' · ');
		return (
			<li key={id}>
				<div className={cn('flex items-center gap-2 rounded px-1 py-1', colors.classes.bgHover)} style={{ paddingLeft: `${depth * 18 + 4}px` }}>
					<button
						type="button"
						aria-label={hasChildren ? (expanded ? t('Collapse') : t('Expand')) : undefined}
						className={cn('flex h-4 w-4 items-center justify-center text-[10px]', colors.classes.textTertiary, hasChildren ? '' : 'invisible')}
						onClick={() =>
							setCollapsed((current) => {
								const next = new Set(current);
								if (next.has(id)) {
									next.delete(id);
								} else {
									next.add(id);
								}
								return next;
							})
						}
					>
						<FontAwesomeIcon icon={expanded ? faChevronDown : faChevronRight} />
					</button>
					<div className="min-w-0 flex-1 truncate">
						<Checkbox checked={checked} disabled={impliedByParent} label={t(node.team.team_name)} onChange={() => toggle(node)} />
					</div>
					{detail !== '' && <span className={cn('shrink-0 text-xs', colors.classes.textTertiary)}>{t(detail)}</span>}
				</div>
				{hasChildren && expanded && (
					<ul>{node.children.map((child) => renderNode(child, depth + 1, includeSubTeams && checked && child.team.is_overlay !== true))}</ul>
				)}
			</li>
		);
	};

	// The filter bar's control height, the same variable MultiSelect uses, so the
	// Teams field lines up with its neighbors.
	const triggerHeight = size === 'xs' ? 'min-h-[var(--control-h-xs,2rem)] text-xs' : size === 'sm' ? 'min-h-[34px] text-sm' : 'min-h-[38px] text-sm';
	const inlinePanel = layout === 'tray' || layout === 'sidebar';

	const panel = (
		<div
			ref={panelRef}
			className={cn('flex flex-col', inlinePanel ? 'mt-2' : cn('fixed z-[1000] w-[340px] rounded-md border shadow-lg', colors.classes.borderDefault, colors.classes.bgPrimary))}
			style={inlinePanel || anchor == null ? undefined : { top: anchor.top, left: anchor.left }}
		>
			<div className={cn('flex items-center gap-2 border-b px-2 py-1.5', colors.classes.borderDefault)}>
				<FontAwesomeIcon icon={faMagnifyingGlass} className={cn('text-xs', colors.classes.textTertiary)} />
				<input
					className={cn('w-full bg-transparent text-sm outline-none', colors.classes.textPrimary)}
					placeholder={t('Search teams')}
					value={search}
					onChange={(event) => setSearch(event.target.value)}
					aria-label={t('Search teams')}
				/>
			</div>
			<ul className="max-h-72 overflow-y-auto px-1 py-1" role="tree" aria-label={t(config.label)}>
				{teams == null ? <li className={cn('px-2 py-2 text-xs', colors.classes.textSecondary)}>{t('Loading...')}</li> : tree.map((node) => renderNode(node, 0, false))}
			</ul>
			<div className={cn('flex items-center justify-between gap-2 border-t px-3 py-2', colors.classes.borderDefault)}>
				<span className="flex items-center gap-2">
					<Toggle checked={includeSubTeams} onChange={setIncludeSubTeams} ariaLabelledBy={`${filterType}-sub-teams`} />
					<span id={`${filterType}-sub-teams`} className={cn('text-xs', colors.classes.textPrimary)}>
						{t('Include sub-teams')}
					</span>
					<Tooltip content={t('On: a region counts every team beneath it. Off: only the people directly on the teams you tick.')}>
						<FontAwesomeIcon icon={faCircleInfo} className={cn('text-xs', colors.classes.textTertiary)} />
					</Tooltip>
				</span>
				{!inlinePanel && (
					<button type="button" className={cn('rounded px-3 py-1 text-xs font-semibold', colors.classes.primaryButton)} onClick={() => setOpen(false)}>
						{t('Done')}
					</button>
				)}
			</div>
		</div>
	);

	return (
		<FormField name={filterType} label={t(config.label)} labelHidden={false} required={config.required} size={size}>
			<div ref={wrapperRef} className="relative">
				{!inlinePanel && (
					<button
						type="button"
						disabled={disabled || isModalOpen}
						aria-haspopup="tree"
						aria-expanded={open}
						onClick={() => setOpen((current) => !current)}
						className={cn('flex w-full items-center gap-1 rounded-md border px-2 text-left', triggerHeight, colors.classes.inputDefault)}
					>
						<span className="flex min-w-0 flex-1 flex-wrap items-center gap-1 py-1">
							{selectedValues.length === 0 ? (
								<span className={colors.classes.textTertiary}>{t('All teams')}</span>
							) : layout === 'bar' && selectedValues.length > 1 ? (
								<span className={colors.classes.textPrimary}>{t('{{count}} teams', { count: selectedValues.length })}</span>
							) : (
								selectedValues.map((option) => (
									<span key={String(option.id)} className={cn('inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs', colors.classes.bgTertiary, colors.classes.textPrimary)}>
										{t(option.label)}
										{!includeSubTeams && <span className={colors.classes.textTertiary}>{t('only')}</span>}
										<span
											role="button"
											tabIndex={0}
											aria-label={t('Remove {{name}}', { name: option.label })}
											onClick={(event) => {
												event.stopPropagation();
												onValuesChange(selectedValues.filter((other) => other !== option));
											}}
											onKeyDown={(event) => {
												if (event.key === 'Enter') {
													event.stopPropagation();
													onValuesChange(selectedValues.filter((other) => other !== option));
												}
											}}
										>
											<FontAwesomeIcon icon={faXmark} />
										</span>
									</span>
								))
							)}
						</span>
						<FontAwesomeIcon icon={faChevronDown} className={cn('text-[10px]', colors.classes.textTertiary)} />
					</button>
				)}
				{inlinePanel ? panel : open && anchor != null ? createPortal(panel, document.body) : null}
			</div>
		</FormField>
	);
}
